import React, { useMemo, useState } from 'react';
import {
  MapPin,
  Banknote,
  School,
  FileText,
  Sparkles,
  CheckCircle2,
  LineChart,
  Shield,
  Globe2,
  Star,
  TrendingUp,
  Clock,
  Info,
  Building2,
  Heart,
  Share2,
  Phone,
  Lightbulb,
  ArrowRight,
  FileDown,
  Users,
  Eye,
  Zap,
  BarChart3
} from 'lucide-react';

const ScreenCard = ({ title, desc, icon: Icon, accent = 'from-blue-50 to-blue-100', children, badge, className = "" }) => (
  <div className={`relative rounded-3xl overflow-hidden border border-gray-200/50 shadow-[0_8px_32px_rgba(0,0,0,0.08)] bg-white/80 backdrop-blur-sm hover:shadow-[0_20px_60px_rgba(0,0,0,0.12)] transition-all duration-500 hover:-translate-y-1 ${className}`}>
    <div className={`absolute inset-x-0 top-0 h-20 bg-gradient-to-r ${accent} opacity-80`} />
    {badge && (
      <div className="absolute top-4 right-4 z-10">
        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-gradient-to-r from-primary-100 to-primary-200 text-primary-800 shadow-lg">
          {badge}
        </span>
      </div>
    )}
    <div className="relative px-8 pt-6">
      <div className="flex items-center gap-4">
        {Icon && (
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-white/90 to-white/70 border border-white/50 shadow-xl flex items-center justify-center backdrop-blur-sm">
            <Icon className="w-7 h-7 text-primary-600" />
          </div>
        )}
        <div>
          <h3 className="text-2xl font-bold text-gray-900 drop-shadow-[0_1px_0_rgba(255,255,255,0.9)]">{title}</h3>
          {desc && <p className="text-gray-600 text-sm mt-2 leading-relaxed">{desc}</p>}
        </div>
      </div>
    </div>
    <div className="relative px-8 pb-8 pt-6">
      {children}
    </div>
  </div>
);

const ProgressBar = ({ value, max = 100, color = "blue", label }) => (
  <div className="space-y-2">
    {label && <div className="flex justify-between text-sm font-medium text-gray-700">
      <span>{label}</span>
      <span>{value}%</span>
    </div>}
    <div className="w-full bg-gray-200/50 rounded-full h-3 overflow-hidden">
      <div 
        className={`h-3 rounded-full bg-gradient-to-r from-${color}-400 to-${color}-600 transition-all duration-1000 ease-out shadow-lg`}
        style={{ width: `${(value / max) * 100}%` }}
      />
    </div>
  </div>
);

export default function App() {
  const [city, setCity] = useState('武汉市');

  // Sheet1 场地信息（示例：仅武汉市有样例，其它城市留空示意）
  const venueData = useMemo(() => ({
    '武汉市': [
      {
        id: 1,
        city: '武汉市',
        parkName: '光谷创业咖啡',
        locationCity: '武汉市',
        address: '武汉市洪山区光谷创业街9栋201室',
        level: '国家级、省级、市级',
        area: '7961.82',
        freeArea: '510',
        facilities: '办公桌椅有、电脑需自备、打印机有',
        services:
          '大学生企业免房租、水电，暂无资金支持；代办业务包括各级创新创业大赛；投融资服务；自有种子资金与孵化基金天使投资服务；信息/创业辅导/创新支持/人员培训/市场营销/管理咨询/专业服务等。',
        contact: '刘鹏 13260566578',
        rating: 4.8,
        reviews: 156,
        tags: ['国家级', '免房租', '投融资']
      },
    ],
    '宜昌市': [],
    '襄阳市': [],
  }), []);

  const cityTabs = ['武汉市', '宜昌市', '襄阳市'];

  const KPIStat = ({ label, value, hint, icon: Icon, accent = 'from-blue-500 to-indigo-600', trend, className = "" }) => (
    <div className={`relative overflow-hidden rounded-3xl border border-gray-200/50 bg-white/80 backdrop-blur-sm hover:shadow-2xl transition-all duration-500 hover:-translate-y-1 ${className}`}>
      <div className={`absolute -inset-20 opacity-10 bg-gradient-to-br ${accent} blur-3xl`} />
      <div className="relative p-8">
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider">{label}</div>
            <div className="mt-3 text-4xl font-extrabold tracking-tight text-gray-900">{value}</div>
            {hint && <div className="mt-2 text-sm text-gray-500">{hint}</div>}
            {trend && (
              <div className="mt-3 flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-green-500" />
                <span className="text-sm text-green-600 font-semibold">{trend}</span>
              </div>
            )}
          </div>
          {Icon && (
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-gray-50/80 to-white/80 border border-gray-200/50 shadow-xl flex items-center justify-center backdrop-blur-sm">
              <Icon className="w-8 h-8 text-primary-600" />
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const HighlightBadge = ({ icon: Icon, text, variant = "default" }) => {
    const variants = {
      default: "bg-white/80 backdrop-blur border border-gray-200/50 text-gray-700 shadow-lg",
      primary: "bg-gradient-to-r from-primary-100 to-primary-200 text-primary-800 border border-primary-200/50 shadow-lg",
      success: "bg-gradient-to-r from-green-100 to-green-200 text-green-800 border border-green-200/50 shadow-lg",
      warning: "bg-gradient-to-r from-yellow-100 to-yellow-200 text-yellow-800 border border-yellow-200/50 shadow-lg",
      purple: "bg-gradient-to-r from-purple-100 to-purple-200 text-purple-800 border border-purple-200/50 shadow-lg"
    };
    
    return (
      <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold ${variants[variant]} transition-all duration-300 hover:scale-105`}>
        {Icon && <Icon className="w-4 h-4" />}
        <span>{text}</span>
      </div>
    );
  };

  const SimpleMap = ({ markers = [] }) => (
    <div className="relative h-96 rounded-3xl bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 border border-blue-100/50 overflow-hidden shadow-2xl">
      <div className="absolute inset-0 bg-gradient-to-br from-blue-100/30 to-indigo-100/30" />
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-100 to-blue-200 border border-blue-200/50 shadow-xl flex items-center justify-center mx-auto mb-6">
            <MapPin className="w-8 h-8 text-blue-600" />
          </div>
          <h3 className="text-xl font-bold text-gray-800 mb-2">地图视图</h3>
          <p className="text-gray-600">点击查看详细位置信息</p>
        </div>
      </div>
      {markers.map((marker, index) => (
        <div
          key={index}
          className="absolute w-5 h-5 bg-gradient-to-br from-red-400 to-red-600 rounded-full border-2 border-white shadow-lg transform -translate-x-1/2 -translate-y-1/2 animate-pulse"
          style={{ left: `${marker.x}%`, top: `${marker.y}%` }}
        />
      ))}
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50/30 to-indigo-50/30">
      {/* 背景装饰 */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-blue-400/20 to-purple-400/20 rounded-full blur-3xl animate-float"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-br from-indigo-400/20 to-pink-400/20 rounded-full blur-3xl animate-float" style={{ animationDelay: '2s' }}></div>
      </div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 relative z-10">
        {/* 项目预览说明 */}
        <div className="relative overflow-hidden rounded-3xl mb-16 border border-gray-200/50 bg-white/90 backdrop-blur-sm shadow-[0_20px_60px_rgba(0,0,0,0.08)] hover:shadow-[0_30px_80px_rgba(0,0,0,0.12)] transition-all duration-500">
          <div className="absolute inset-0 bg-gradient-to-r from-blue-50/80 via-indigo-50/80 to-purple-50/80" />
          <div className="absolute -inset-24 opacity-20 bg-[radial-gradient(30rem_15rem_at_25%_20%,rgba(59,130,246,0.6),transparent),radial-gradient(25rem_12rem_at_85%_35%,rgba(168,85,247,0.5),transparent)]" />
          
          {/* 技术支持信息 - 右上角 */}
          <div className="absolute top-6 right-6 z-20">
            <div className="bg-white/95 backdrop-blur-sm border border-gray-200/50 rounded-2xl px-6 py-3 shadow-lg hover:shadow-xl transition-all duration-300">
              <div className="flex items-center gap-3">
                <div className="w-3 h-3 rounded-full bg-gradient-to-r from-primary-500 to-primary-600 animate-pulse"></div>
                <span className="text-base font-medium text-gray-700">
                  技术支持：
                </span>
                <span className="text-base font-semibold text-primary-600">
                  灵衍未来（武汉市）科技有限公司
                </span>
              </div>
            </div>
          </div>
          
          <div className="relative p-8 md:p-12">
            {/* 项目标识 */}
            <div className="flex items-center gap-3 text-primary-700 text-sm font-semibold mb-6">
              <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-primary-100 to-primary-200 border border-primary-200/50 shadow-lg flex items-center justify-center animate-pulse-slow">
                <Sparkles className="w-4 h-4" />
              </div>
              <span>效果预览</span>
              <span className="px-3 py-1.5 bg-gradient-to-r from-primary-100 to-primary-200 text-primary-800 rounded-full text-xs font-bold shadow-lg hover:scale-105 transition-transform duration-300">湖北-四清单两名录</span>
            </div>
            
            {/* 项目标题和说明 */}
            <div className="max-w-4xl">
              <h1 className="text-5xl md:text-6xl font-extrabold tracking-tight text-gray-900 leading-tight mb-8 fade-in">
                湖北-四清单两名录
                <span className="block bg-gradient-to-r from-primary-600 via-indigo-600 to-purple-600 bg-clip-text text-transparent animate-pulse-slow">
                  可视化管理系统效果预览
                </span>
              </h1>
              
              <p className="text-lg text-gray-700 leading-relaxed mb-8">
                本页面为"湖北-四清单两名录"可视化管理系统的效果预览，展示政策清单、项目清单、任务清单、问题清单与企业名录、人才名录的可视化与管理功能。旨在为创业者提供场地资源、融资支持、培训服务、政策清单、导师名录、公共机构等全方位服务的演示效果。
              </p>
              <div className="flex flex-wrap items-center gap-4">
                <HighlightBadge icon={LineChart} text="数据可视化" variant="primary" />
                <HighlightBadge icon={Shield} text="效果预览" variant="success" />
                <HighlightBadge icon={Globe2} text="多端适配" variant="warning" />
                <HighlightBadge icon={Star} text="专业设计" variant="purple" />
              </div>
            </div>
            
            {/* 核心功能预览 */}
            <div className="space-y-6">
              <h3 className="text-xl font-bold text-gray-900 mb-6">核心功能模块</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white/80 backdrop-blur rounded-2xl p-6 border border-white/50 shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 hover-lift">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-blue-100 to-blue-200 border border-blue-200/50 shadow-lg flex items-center justify-center">
                      <MapPin className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <div className="text-base font-bold text-gray-900">场地资源</div>
                      <div className="text-sm text-gray-600">地图展示、详情查看</div>
                    </div>
                  </div>
                </div>
                <div className="bg-white/80 backdrop-blur rounded-2xl p-6 border border-white/50 shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 hover-lift">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-emerald-100 to-emerald-200 border border-emerald-200/50 shadow-lg flex items-center justify-center">
                      <Banknote className="w-6 h-6 text-emerald-600" />
                    </div>
                    <div>
                      <div className="text-base font-bold text-gray-900">融资支持</div>
                      <div className="text-sm text-gray-600">政策查询、在线申请</div>
                    </div>
                  </div>
                </div>
                <div className="bg-white/80 backdrop-blur rounded-2xl p-6 border border-white/50 shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 hover-lift">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-amber-100 to-amber-200 border border-amber-200/50 shadow-lg flex items-center justify-center">
                      <School className="w-6 h-6 text-amber-600" />
                    </div>
                    <div>
                      <div className="text-base font-bold text-gray-900">培训服务</div>
                      <div className="text-sm text-gray-600">课程管理、报名系统</div>
                    </div>
                  </div>
                </div>
                <div className="bg-white/80 backdrop-blur rounded-2xl p-6 border border-white/50 shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 hover-lift">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-purple-100 to-purple-200 border border-purple-200/50 shadow-lg flex items-center justify-center">
                      <FileText className="w-6 h-6 text-purple-600" />
                    </div>
                    <div>
                      <div className="text-base font-bold text-gray-900">政策清单</div>
                      <div className="text-sm text-gray-600">政策发布、查询</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* 技术特色 */}
            <div className="mt-12 p-8 rounded-2xl bg-white/80 backdrop-blur border border-white/50 shadow-xl hover-lift">
              <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-primary-100 to-primary-200 border border-primary-200/50 shadow-lg flex items-center justify-center">
                  <Zap className="w-4 h-4 text-primary-600" />
                </div>
                技术特色
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-sm text-gray-700">
                <div className="flex items-center gap-3">
                  <div className="w-6 h-6 rounded-full bg-gradient-to-br from-green-100 to-green-200 border border-green-200/50 shadow-lg flex items-center justify-center">
                    <CheckCircle2 className="w-3 h-3 text-green-600" />
                  </div>
                  <span className="font-semibold">现代化React技术栈，响应式设计</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-6 h-6 rounded-full bg-gradient-to-br from-green-100 to-green-200 border border-green-200/50 shadow-lg flex items-center justify-center">
                    <CheckCircle2 className="w-3 h-3 text-green-600" />
                  </div>
                  <span className="font-semibold">高德地图集成，精准位置服务</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-6 h-6 rounded-full bg-gradient-to-br from-green-100 to-green-200 border border-green-200/50 shadow-lg flex items-center justify-center">
                    <CheckCircle2 className="w-3 h-3 text-green-600" />
                  </div>
                  <span className="font-semibold">数据可视化图表，直观展示</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 总览KPI */}
        <section id="overview" className="mb-16">
          <div className="mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-4 flex items-center gap-3">
              <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-primary-100 to-primary-200 border border-primary-200/50 shadow-lg flex items-center justify-center">
                <BarChart3 className="w-4 h-4 text-primary-600" />
              </div>
              数据总览
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed">演示数据展示，支持多维度分析。以下数据为"湖北-四清单两名录"可视化管理系统的演示效果，涵盖场地资源、融资支持、培训服务、政策清单、导师名录、公共机构等六大核心板块。</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            <KPIStat label="园区/场地数量" value="86" hint="覆盖全省主要城市" icon={MapPin} trend="+12%" />
            <KPIStat label="可入驻面积" value="12.5万㎡" hint="空余面积演示数据" icon={Building2} accent="from-violet-500 to-fuchsia-600" trend="+8%" />
            <KPIStat label="可申领额度" value="5亿元+" hint="含担保与贴息" icon={Banknote} accent="from-emerald-500 to-teal-600" trend="+15%" />
            <KPIStat label="年度培训场次" value="320+" hint="多对象分类培训" icon={School} accent="from-amber-500 to-orange-600" trend="+20%" />
          </div>
          <div className="mt-8 p-8 rounded-2xl bg-gradient-to-r from-blue-50/80 to-indigo-50/80 border border-blue-100/50 shadow-xl">
            <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-3">
              <div className="w-6 h-6 rounded-lg bg-gradient-to-br from-blue-100 to-blue-200 border border-blue-200/50 shadow-lg flex items-center justify-center">
                <Info className="w-3 h-3 text-blue-600" />
              </div>
              数据说明
            </h3>
            <div className="text-sm text-gray-700 leading-relaxed space-y-3">
              <p><strong>数据来源：</strong>演示数据，基于湖北省人力资源和社会保障厅、各地市州人社部门、金融机构、培训机构等官方渠道的模拟数据。</p>
              <p><strong>更新频率：</strong>演示数据，实际项目中场地信息每日更新，融资政策实时同步，培训服务每周更新，政策清单月度更新。</p>
              <p><strong>统计口径：</strong>演示数据，覆盖湖北省17个市州，包含国家级、省级、市级各类园区和机构的模拟数据。</p>
              <p><strong>使用说明：</strong>以上为演示数据，用于呈现视觉与信息布局能力，实际数据接入后自动驱动。</p>
            </div>
          </div>
        </section>

        {/* 右侧浮动导航（锚点） */}
        <div className="hidden lg:flex fixed right-6 top-1/3 z-10 flex-col gap-3">
          <a href="#overview" className="group px-4 py-3 rounded-2xl bg-white/90 backdrop-blur-sm border border-gray-200/50 text-sm font-semibold text-gray-700 hover:bg-white hover:shadow-2xl transition-all duration-300 hover:-translate-x-1">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-primary-500 group-hover:scale-150 transition-transform duration-300"></div>
              总览
            </div>
          </a>
          <a href="#venues" className="group px-4 py-3 rounded-2xl bg-white/90 backdrop-blur-sm border border-gray-200/50 text-sm font-semibold text-gray-700 hover:bg-white hover:shadow-2xl transition-all duration-300 hover:-translate-x-1">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-blue-500 group-hover:scale-150 transition-transform duration-300"></div>
              场地资源
            </div>
          </a>
          <a href="#financing" className="group px-4 py-3 rounded-2xl bg-white/90 backdrop-blur-sm border border-gray-200/50 text-sm font-semibold text-gray-700 hover:bg-white hover:shadow-2xl transition-all duration-300 hover:-translate-x-1">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-emerald-500 group-hover:scale-150 transition-transform duration-300"></div>
              融资支持
            </div>
          </a>
          <a href="#training" className="group px-4 py-3 rounded-2xl bg-white/90 backdrop-blur-sm border border-gray-200/50 text-sm font-semibold text-gray-700 hover:bg-white hover:shadow-2xl transition-all duration-300 hover:-translate-x-1">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-amber-500 group-hover:scale-150 transition-transform duration-300"></div>
              培训服务
            </div>
          </a>
          <a href="#policies" className="group px-4 py-3 rounded-2xl bg-white/90 backdrop-blur-sm border border-gray-200/50 text-sm font-semibold text-gray-700 hover:bg-white hover:shadow-2xl transition-all duration-300 hover:-translate-x-1">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-purple-500 group-hover:scale-150 transition-transform duration-300"></div>
              政策清单
            </div>
          </a>
          <a href="#mentors" className="group px-4 py-3 rounded-2xl bg-white/90 backdrop-blur-sm border border-gray-200/50 text-sm font-semibold text-gray-700 hover:bg-white hover:shadow-2xl transition-all duration-300 hover:-translate-x-1">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-cyan-500 group-hover:scale-150 transition-transform duration-300"></div>
              创业导师
            </div>
          </a>
          <a href="#agencies" className="group px-4 py-3 rounded-2xl bg-white/90 backdrop-blur-sm border border-gray-200/50 text-sm font-semibold text-gray-700 hover:bg-white hover:shadow-2xl transition-all duration-300 hover:-translate-x-1">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-slate-500 group-hover:scale-150 transition-transform duration-300"></div>
              公共机构
            </div>
          </a>
        </div>

        {/* Section 1: 场地资源（整屏高度） */}
        <section id="venues" className="min-h-screen py-12 scroll-mt-6">
          <ScreenCard title="场地资源地图" desc="支持城市切换、地图标记与关键信息卡片；用于展示可供入驻的园区/场地。" icon={MapPin} accent="from-blue-50 to-indigo-50" badge="热门">
            <div className="mb-6 p-6 rounded-xl bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-100">
              <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                <Info className="w-4 h-4" />
                功能说明
              </h3>
              <div className="text-sm text-gray-700 leading-relaxed space-y-2">
                <p><strong>地图展示：</strong>采用交互式地图展示，支持城市切换、区域筛选、关键词搜索等功能。地图标记采用动态效果，点击可查看详细信息。</p>
                <p><strong>信息卡片：</strong>每个场地都有详细的信息卡片，包含场地名称、级别、面积、设施、政策服务、联系方式等关键信息。</p>
                <p><strong>筛选功能：</strong>支持按城市、级别、面积区间、设施类型、政策类型等多维度筛选，帮助用户快速找到合适的场地。</p>
                <p><strong>操作功能：</strong>支持收藏、分享、对比、快速联系等操作，提升用户体验。</p>
              </div>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div>
                <div className="mb-6 flex gap-2">
                  {cityTabs.map((c) => (
                    <button
                      key={c}
                      onClick={() => setCity(c)}
                      className={`px-4 py-2 rounded-full text-sm font-medium border transition-all duration-300 ${
                        city === c 
                          ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white border-blue-600 shadow-lg' 
                          : 'bg-white text-gray-700 border-gray-200 hover:border-gray-300 hover:shadow-md'
                      }`}
                    >{c}</button>
                  ))}
                </div>
                <SimpleMap markers={city === '武汉市' ? [{ x: 62, y: 58, label: '光谷创业咖啡' }] : []} />
              </div>
              <div className="space-y-4">
                {venueData[city].length === 0 && (
                  <div className="text-center py-8">
                    <MapPin className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                    <p className="text-gray-400">暂无该城市场地数据（占位演示）</p>
                    <p className="text-sm text-gray-500 mt-2">实际项目中将根据数据导入情况动态显示</p>
                  </div>
                )}
                {venueData[city].map((v) => (
                  <div key={v.id} className="border border-gray-200 rounded-xl p-6 bg-white hover:shadow-lg transition-all duration-300">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <div className="text-xl font-bold text-gray-900">{v.parkName}</div>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {v.level}
                          </span>
                          <div className="flex items-center gap-1">
                            <Star className="w-4 h-4 text-yellow-400 fill-current" />
                            <span className="text-sm font-medium text-gray-900">{v.rating}</span>
                            <span className="text-sm text-gray-500">({v.reviews}条评价)</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button className="p-2 rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors">
                          <Heart className="w-4 h-4 text-gray-600" />
                        </button>
                        <button className="p-2 rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors">
                          <Share2 className="w-4 h-4 text-gray-600" />
                        </button>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4 text-sm text-gray-700">
                      <div>
                        <div className="text-gray-500 font-medium">所在城市</div>
                        <div className="font-semibold">{v.locationCity}</div>
                      </div>
                      <div>
                        <div className="text-gray-500 font-medium">联系信息</div>
                        <div className="font-semibold">{v.contact}</div>
                      </div>
                      <div className="col-span-2">
                        <div className="text-gray-500 font-medium">详细地址</div>
                        <div className="font-semibold">{v.address}</div>
                      </div>
                      <div>
                        <div className="text-gray-500 font-medium">场地面积(㎡)</div>
                        <div className="font-semibold">{v.area}</div>
                      </div>
                      <div>
                        <div className="text-gray-500 font-medium">空余面积(㎡)</div>
                        <div className="font-semibold text-green-600">{v.freeArea}</div>
                      </div>
                      <div className="col-span-2">
                        <div className="text-gray-500 font-medium">设施设备</div>
                        <div className="font-semibold">{v.facilities}</div>
                      </div>
                      <div className="col-span-2">
                        <div className="text-gray-500 font-medium">政策/服务</div>
                        <div className="leading-relaxed">{v.services}</div>
                      </div>
                    </div>
                    <div className="mt-4 flex gap-2">
                      <button className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all duration-300">
                        <Phone className="w-4 h-4" /> 快速联系
                      </button>
                      <button className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 shadow-lg hover:shadow-xl transition-all duration-300">
                        <Heart className="w-4 h-4" /> 收藏
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="mt-6 p-6 rounded-xl bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-100 text-blue-800 text-sm leading-relaxed">
              <div className="font-bold mb-2 flex items-center gap-2">
                <Lightbulb className="w-4 h-4" />
                设计说明
              </div>
              <ul className="list-disc pl-5 space-y-1">
                <li><strong>城市切换：</strong>支持武汉、宜昌、襄阳等主要城市切换，标记与卡片联动；后续可接入真实地图 SDK。</li>
                <li><strong>筛选功能：</strong>支持按类别、面积区间、设施类型、政策类型等多维度筛选。</li>
                <li><strong>交互体验：</strong>卡片支持收藏、对比、分享与快捷联系，提升用户操作便利性。</li>
                <li><strong>数据展示：</strong>新增评分系统、标签分类、用户评价等社交化功能，增强信息可信度。</li>
                <li><strong>扩展性：</strong>支持更多城市数据接入，可根据实际需求调整展示内容和交互方式。</li>
              </ul>
            </div>
          </ScreenCard>
        </section>

        {/* Section 2: 融资支持 */}
        <section id="financing" className="min-h-screen py-12 scroll-mt-6">
          <ScreenCard title="融资支持方案" desc="展示政策条款、额度与贴息规则；配机构信息、联系人与线上申请链接。" icon={Banknote} accent="from-emerald-50 to-teal-50" badge="推荐">
            <div className="mb-6 p-6 rounded-xl bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-100">
              <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                <Info className="w-4 h-4" />
                功能说明
              </h3>
              <div className="text-sm text-gray-700 leading-relaxed space-y-2">
                <p><strong>政策展示：</strong>详细展示各类融资政策条款、申请条件、额度限制、贴息规则等关键信息，帮助用户快速了解政策要点。</p>
                <p><strong>机构信息：</strong>提供金融机构的详细信息，包括地址、联系方式、服务范围等，便于用户直接联系。</p>
                <p><strong>申请流程：</strong>支持在线申请、材料下载、进度跟踪等功能，实现从了解政策到完成申请的闭环。</p>
                <p><strong>数据统计：</strong>展示申请成功率、平均审批时间、额度分布等统计信息，帮助用户做出决策。</p>
              </div>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 border border-gray-200 rounded-xl p-6 bg-white hover:shadow-lg transition-all duration-300">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <div className="text-lg font-bold text-gray-900">武汉农村商业银行</div>
                    <div className="text-sm text-gray-600">地址：武汉市江岸区建设大道618号武银大厦</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800">
                      推荐机构
                    </span>
                  </div>
                </div>
                <div className="prose prose-sm text-gray-700 leading-relaxed">
                  <p><strong>政策概述：</strong>一年内，新招用符合创业担保贷款申请条件的人数，达企业在职职工10%（超100人企业达5%）且签1年以上劳动合同。企业须无拖欠工资、欠缴社保等违法违规信用记录。</p>
                  <p><strong>额度限制：</strong>小微企业贷款额度不超500万元，期限不超2年。对符合条件的，贷款利率上限为LPR+150BPs。</p>
                  <p><strong>贴息政策：</strong>财政部门按照实际贷款利率的50%给予贴息。创业担保贷款累计可享受不超3次。</p>
                </div>
                <div className="mt-4 text-sm text-gray-600">
                  <strong>经融联系人：</strong>陈昕 027-85497387<br/>
                  <strong>人社联系人：</strong>张曙宇 027-85805987
                </div>
                <div className="mt-6 flex gap-3">
                  <button className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-emerald-600 to-teal-600 text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105">
                    网上申请 <ArrowRight className="w-4 h-4" />
                  </button>
                  <button className="inline-flex items-center gap-2 px-6 py-3 rounded-lg border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 shadow-lg hover:shadow-xl transition-all duration-300">
                    <FileDown className="w-4 h-4" /> 下载材料清单
                  </button>
                </div>
              </div>
              <div className="space-y-4">
                <div className="border border-gray-200 rounded-xl p-4 bg-white">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-semibold text-gray-900">申请进度</h4>
                    <span className="text-sm text-gray-500">演示数据</span>
                  </div>
                  <div className="space-y-3">
                    <div>
                      <div className="flex justify-between text-sm mb-1">
                        <span>材料审核</span>
                        <span className="text-green-600 font-medium">已完成</span>
                      </div>
                      <ProgressBar value={100} color="green" />
                    </div>
                    <div>
                      <div className="flex justify-between text-sm mb-1">
                        <span>额度评估</span>
                        <span className="text-blue-600 font-medium">进行中</span>
                      </div>
                      <ProgressBar value={65} color="blue" />
                    </div>
                    <div>
                      <div className="flex justify-between text-sm mb-1">
                        <span>最终审批</span>
                        <span className="text-gray-500">待开始</span>
                      </div>
                      <ProgressBar value={0} color="gray" />
                    </div>
                  </div>
                </div>
                <div className="border border-gray-200 rounded-xl p-4 bg-white">
                  <h4 className="font-semibold text-gray-900 mb-3">快速统计</h4>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">平均审批时间</span>
                      <span className="font-semibold">7-15天</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">成功率</span>
                      <span className="font-semibold text-green-600">85%</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">平均额度</span>
                      <span className="font-semibold">200万</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-6 p-6 rounded-xl bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-100 text-emerald-800 text-sm leading-relaxed">
              <div className="font-bold mb-2 flex items-center gap-2">
                <Lightbulb className="w-4 h-4" />
                设计说明
              </div>
              <ul className="list-disc pl-5 space-y-1">
                <li><strong>政策展示：</strong>卡片化条款展示，突出关键信息；提供"网上申请"与资料下载功能。</li>
                <li><strong>申请流程：</strong>可拓展额度计算器与资格自测表单，形成从了解→申请的闭环。</li>
                <li><strong>进度跟踪：</strong>新增申请进度跟踪、成功率统计、审批时间预估等功能。</li>
                <li><strong>用户体验：</strong>支持筛选与收藏，提供多种联系方式，确保用户能够快速获得帮助。</li>
                <li><strong>数据驱动：</strong>基于真实数据统计，提供决策参考，帮助用户选择最适合的融资方案。</li>
              </ul>
            </div>
          </ScreenCard>
        </section>

        {/* Section 3: 培训服务 */}
        <section id="training" className="min-h-screen py-12 scroll-mt-6">
          <ScreenCard title="培训服务" desc="展示培训机构、地点、课程内容、对象与期数；支持报名/咨询。" icon={School} accent="from-amber-50 to-orange-50" badge="热门">
            <div className="mb-6 p-6 rounded-xl bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-100">
              <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                <Info className="w-4 h-4" />
                功能说明
              </h3>
              <div className="text-sm text-gray-700 leading-relaxed space-y-2">
                <p><strong>培训展示：</strong>全面展示各类培训机构信息，包括机构名称、地址、联系方式、培训内容、对象、期数等详细信息。</p>
                <p><strong>课程管理：</strong>支持课程分类、档期安排、名额管理等功能，帮助用户了解培训安排和报名情况。</p>
                <p><strong>报名功能：</strong>提供在线报名、咨询电话、课程详情查看等功能，实现培训服务的全流程管理。</p>
                <p><strong>数据统计：</strong>展示培训统计信息，包括累计培训人数、就业率、满意度等关键指标。</p>
              </div>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 border border-gray-200 rounded-xl p-6 bg-white hover:shadow-lg transition-all duration-300">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <div className="text-lg font-bold text-gray-900">武汉伟鼎职业培训学校</div>
                    <div className="text-sm text-gray-600">地点：武汉市江汉区经济开发区智慧大厦汉口创业中心二号楼</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                      热门课程
                    </span>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4 text-sm text-gray-700 mb-4">
                  <div>
                    <div className="text-gray-500 font-medium">培训内容</div>
                    <div className="font-semibold">SYB培训、网络创业培训</div>
                  </div>
                  <div>
                    <div className="text-gray-500 font-medium">期数</div>
                    <div className="font-semibold">4期</div>
                  </div>
                  <div className="col-span-2">
                    <div className="text-gray-500 font-medium">培训对象</div>
                    <div className="font-semibold">城镇登记失业人员、农村转移就业劳动者、毕业年度高校毕业生、初期创业者等</div>
                  </div>
                  <div>
                    <div className="text-gray-500 font-medium">联系人</div>
                    <div className="font-semibold">13477024007</div>
                  </div>
                  <div>
                    <div className="text-gray-500 font-medium">剩余名额</div>
                    <div className="font-semibold text-green-600">23人</div>
                  </div>
                </div>
                <div className="flex gap-3">
                  <button className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-amber-600 to-orange-600 text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105">
                    立即报名 <ArrowRight className="w-4 h-4" />
                  </button>
                  <button className="inline-flex items-center gap-2 px-6 py-3 rounded-lg border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 shadow-lg hover:shadow-xl transition-all duration-300">
                    <Phone className="w-4 h-4" /> 咨询电话
                  </button>
                </div>
              </div>
              <div className="space-y-4">
                <div className="border border-gray-200 rounded-xl p-4 bg-white">
                  <h4 className="font-semibold text-gray-900 mb-3">课程安排</h4>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div>
                        <div className="font-medium text-gray-900">SYB创业培训</div>
                        <div className="text-sm text-gray-600">10天课程</div>
                      </div>
                      <span className="text-sm text-green-600 font-medium">进行中</span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div>
                        <div className="font-medium text-gray-900">网络创业培训</div>
                        <div className="text-sm text-gray-600">7天课程</div>
                      </div>
                      <span className="text-sm text-blue-600 font-medium">即将开始</span>
                    </div>
                  </div>
                </div>
                <div className="border border-gray-200 rounded-xl p-4 bg-white">
                  <h4 className="font-semibold text-gray-900 mb-3">培训统计</h4>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">累计培训人数</span>
                      <span className="font-semibold">1,234人</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">就业率</span>
                      <span className="font-semibold text-green-600">78%</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">满意度</span>
                      <span className="font-semibold text-yellow-600">4.6/5.0</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-6 p-6 rounded-xl bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-100 text-amber-800 text-sm leading-relaxed">
              <div className="font-bold mb-2 flex items-center gap-2">
                <Lightbulb className="w-4 h-4" />
                设计说明
              </div>
              <ul className="list-disc pl-5 space-y-1">
                <li><strong>培训展示：</strong>支持报名/咨询按钮、档期与名额展示；管理员侧收到报名通知。</li>
                <li><strong>筛选功能：</strong>按城市、课程类型、对象筛选；过期场次自动弱化处理。</li>
                <li><strong>课程管理：</strong>新增课程安排、培训统计、满意度评价等功能。</li>
                <li><strong>用户体验：</strong>提供详细的课程信息、培训对象、联系方式等，便于用户做出选择。</li>
                <li><strong>数据驱动：</strong>基于真实培训数据，展示培训效果和用户满意度，提升可信度。</li>
              </ul>
            </div>
          </ScreenCard>
        </section>

        {/* Section 4: 政策清单 */}
        <section id="policies" className="min-h-screen py-12 scroll-mt-6">
          <ScreenCard title="创业扶持政策" desc="展示政策名称、设定依据、服务对象与支持方式；支持附件与解读。" icon={FileText} accent="from-violet-50 to-fuchsia-50" badge="最新">
            <div className="mb-6 p-6 rounded-xl bg-gradient-to-r from-violet-50 to-fuchsia-50 border border-violet-100">
              <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                <Info className="w-4 h-4" />
                功能说明
              </h3>
              <div className="text-sm text-gray-700 leading-relaxed space-y-2">
                <p><strong>政策展示：</strong>全面展示各类创业扶持政策，包括政策名称、设定依据、服务对象、支持方式等详细信息。</p>
                <p><strong>分类管理：</strong>支持按政策类型、级别、有效期等多维度分类，帮助用户快速找到相关政策。</p>
                <p><strong>附件下载：</strong>提供政策原文链接、附件下载、政策解读等功能，确保用户能够获得完整信息。</p>
                <p><strong>统计信息：</strong>展示政策总数、新增政策、即将到期政策等统计信息，帮助用户了解政策动态。</p>
              </div>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 border border-gray-200 rounded-xl p-6 bg-white hover:shadow-lg transition-all duration-300">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <div className="text-lg font-bold text-gray-900">湖北省大学生创业孵化园</div>
                    <div className="text-sm text-gray-600">设定依据：鄂人社办函〔2024〕45号 等</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-violet-100 text-violet-800">
                      最新政策
                    </span>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4 text-sm text-gray-700 mb-4">
                  <div>
                    <div className="text-gray-500 font-medium">服务对象</div>
                    <div className="font-semibold">各市（州）人社部门申报基本条件，对所属创业孵化基地进行考评择优</div>
                  </div>
                  <div>
                    <div className="text-gray-500 font-medium">政策级别</div>
                    <div className="font-semibold">省级</div>
                  </div>
                  <div className="col-span-2">
                    <div className="text-gray-500 font-medium">支持方式</div>
                    <div className="font-semibold">按基地规模、入驻实体数量和孵化效果给予50万元、80万元奖补；被认定为全国创业孵化示范基地的，再给予100万元一次性奖补。</div>
                  </div>
                </div>
                <div className="flex gap-3">
                  <button className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105">
                    查看原文 <ArrowRight className="w-4 h-4" />
                  </button>
                  <button className="inline-flex items-center gap-2 px-6 py-3 rounded-lg border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 shadow-lg hover:shadow-xl transition-all duration-300">
                    <FileDown className="w-4 h-4" /> 下载附件
                  </button>
                </div>
              </div>
              <div className="space-y-4">
                <div className="border border-gray-200 rounded-xl p-4 bg-white">
                  <h4 className="font-semibold text-gray-900 mb-3">政策分类</h4>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                      <span className="text-sm font-medium">创业孵化</span>
                      <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">12条</span>
                    </div>
                    <div className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                      <span className="text-sm font-medium">融资支持</span>
                      <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">8条</span>
                    </div>
                    <div className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                      <span className="text-sm font-medium">人才引进</span>
                      <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full">6条</span>
                    </div>
                    <div className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                      <span className="text-sm font-medium">税收优惠</span>
                      <span className="text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded-full">4条</span>
                    </div>
                  </div>
                </div>
                <div className="border border-gray-200 rounded-xl p-4 bg-white">
                  <h4 className="font-semibold text-gray-900 mb-3">政策统计</h4>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">总政策数</span>
                      <span className="font-semibold">30条</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">本月新增</span>
                      <span className="font-semibold text-green-600">+3条</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">即将到期</span>
                      <span className="font-semibold text-orange-600">2条</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-6 p-6 rounded-xl bg-gradient-to-r from-violet-50 to-fuchsia-50 border border-violet-100 text-violet-800 text-sm leading-relaxed">
              <div className="font-bold mb-2 flex items-center gap-2">
                <Lightbulb className="w-4 h-4" />
                设计说明
              </div>
              <ul className="list-disc pl-5 space-y-1">
                <li><strong>政策展示：</strong>提供设定依据原文链接与附件下载；政策解读与常见问题区域。</li>
                <li><strong>分类管理：</strong>按对象、级别、有效期筛选；收藏、对比与订阅更新。</li>
                <li><strong>统计信息：</strong>新增政策分类、统计信息、到期提醒等功能。</li>
                <li><strong>用户体验：</strong>支持政策搜索、筛选、收藏等功能，确保用户能够快速找到所需政策。</li>
                <li><strong>信息完整：</strong>提供政策原文、附件下载、解读说明等完整信息，确保政策传达的准确性。</li>
              </ul>
            </div>
          </ScreenCard>
        </section>

        {/* Section 5: 创业导师 */}
        <section id="mentors" className="min-h-screen py-12 scroll-mt-6">
          <ScreenCard title="创业导师" desc="导师卡片化展示；支持检索、标签筛选与预约。" icon={Users} accent="from-cyan-50 to-sky-50" badge="推荐">
            <div className="mb-6 p-6 rounded-xl bg-gradient-to-r from-cyan-50 to-sky-50 border border-cyan-100">
              <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                <Info className="w-4 h-4" />
                功能说明
              </h3>
              <div className="text-sm text-gray-700 leading-relaxed space-y-2">
                <p><strong>导师展示：</strong>全面展示创业导师信息，包括姓名、单位、专业方向、联系方式、服务案例等详细信息。</p>
                <p><strong>分类筛选：</strong>支持按领域、行业、地区等标签筛选，帮助用户快速找到合适的导师。</p>
                <p><strong>预约功能：</strong>提供预约咨询、在线咨询、案例查看等功能，实现导师服务的全流程管理。</p>
                <p><strong>评价系统：</strong>展示导师评分、服务案例、用户评价等信息，帮助用户做出选择。</p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="border border-gray-200 rounded-xl p-6 bg-white hover:shadow-lg transition-all duration-300">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <div className="text-lg font-bold text-gray-900">艾靓</div>
                    <div className="text-sm text-gray-600">武汉理工大学创业学院</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-cyan-100 text-cyan-800">
                      推荐导师
                    </span>
                  </div>
                </div>
                <div className="space-y-3 text-sm text-gray-700 mb-4">
                  <div>
                    <div className="text-gray-500 font-medium">专业方向</div>
                    <div className="font-semibold">创业指导、政策咨询</div>
                  </div>
                  <div>
                    <div className="text-gray-500 font-medium">联系电话</div>
                    <div className="font-semibold">18971295577</div>
                  </div>
                  <div>
                    <div className="text-gray-500 font-medium">服务案例</div>
                    <div className="font-semibold text-green-600">156个成功案例</div>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 bg-gradient-to-r from-cyan-600 to-sky-600 text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all duration-300">
                    预约咨询 <ArrowRight className="w-4 h-4" />
                  </button>
                  <button className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 shadow-lg hover:shadow-xl transition-all duration-300">
                    <Eye className="w-4 h-4" /> 查看案例
                  </button>
                </div>
              </div>
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="border border-gray-200 rounded-xl p-6 bg-white hover:shadow-lg transition-all duration-300">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <div className="h-5 bg-gray-200 rounded w-2/3 mb-2 animate-pulse" />
                      <div className="h-4 bg-gray-200 rounded w-4/5 animate-pulse" />
                    </div>
                    <div className="h-6 bg-gray-200 rounded w-16 animate-pulse" />
                  </div>
                  <div className="space-y-3 mb-4">
                    <div className="h-4 bg-gray-200 rounded w-3/4 animate-pulse" />
                    <div className="h-4 bg-gray-200 rounded w-1/2 animate-pulse" />
                    <div className="h-4 bg-gray-200 rounded w-2/3 animate-pulse" />
                  </div>
                  <div className="flex gap-2">
                    <div className="flex-1 h-10 bg-gray-200 rounded-lg animate-pulse" />
                    <div className="w-20 h-10 bg-gray-200 rounded-lg animate-pulse" />
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-6 p-6 rounded-xl bg-gradient-to-r from-cyan-50 to-sky-50 border border-cyan-100 text-cyan-800 text-sm leading-relaxed">
              <div className="font-bold mb-2 flex items-center gap-2">
                <Lightbulb className="w-4 h-4" />
                设计说明
              </div>
              <ul className="list-disc pl-5 space-y-1">
                <li><strong>导师展示：</strong>标签（领域/行业/地区）筛选；可展示导师成果与案例。</li>
                <li><strong>预约功能：</strong>预约对接：可预约时段、在线咨询与成果沉淀。</li>
                <li><strong>评价系统：</strong>新增导师评分、服务案例、专业领域标签等功能。</li>
                <li><strong>用户体验：</strong>提供详细的导师信息、专业方向、联系方式等，便于用户选择合适的导师。</li>
                <li><strong>服务管理：</strong>支持预约管理、咨询记录、案例展示等功能，提升服务质量。</li>
              </ul>
            </div>
          </ScreenCard>
        </section>

        {/* Section 6: 公共就业服务机构 */}
        <section id="agencies" className="min-h-screen py-12 scroll-mt-6">
          <ScreenCard title="公共就业服务机构" desc="展示机构名称、地址与联系方式；支持地图定位与一键拨号。" icon={Building2} accent="from-slate-50 to-gray-50" badge="演示">
            <div className="mb-6 p-6 rounded-xl bg-gradient-to-r from-slate-50 to-gray-50 border border-slate-200">
              <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                <Info className="w-4 h-4" />
                功能说明
              </h3>
              <div className="text-sm text-gray-700 leading-relaxed space-y-2">
                <p><strong>机构展示：</strong>全面展示公共就业服务机构信息，包括机构名称、地址、联系方式、营业时间、服务类型等详细信息。</p>
                <p><strong>服务分类：</strong>支持按城市、服务类型、营业时间等维度筛选，帮助用户快速找到合适的服务机构。</p>
                <p><strong>便捷联系：</strong>提供一键拨号、地图定位、办事指南等功能，便于用户快速联系和获取服务。</p>
                <p><strong>统计信息：</strong>展示服务统计信息，包括月服务人次、成功率、平均等待时间等关键指标。</p>
              </div>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 border border-gray-200 rounded-xl p-6 bg-white hover:shadow-lg transition-all duration-300">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <div className="text-lg font-bold text-gray-900">江岸区公共就业和人才服务中心</div>
                    <div className="text-sm text-gray-600">所在城市：武汉市</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-800">
                      演示机构
                    </span>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4 text-sm text-gray-700 mb-4">
                  <div>
                    <div className="text-gray-500 font-medium">详细地址</div>
                    <div className="font-semibold">武汉市江岸区麟趾路50号</div>
                  </div>
                  <div>
                    <div className="text-gray-500 font-medium">联系电话</div>
                    <div className="font-semibold">027-82732315</div>
                  </div>
                  <div>
                    <div className="text-gray-500 font-medium">营业时间</div>
                    <div className="font-semibold">周一至周五 9:00-17:00</div>
                  </div>
                  <div>
                    <div className="text-gray-500 font-medium">服务类型</div>
                    <div className="font-semibold">就业指导、人才服务</div>
                  </div>
                </div>
                <div className="flex gap-3">
                  <button className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-slate-600 to-gray-600 text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105">
                    <Phone className="w-4 h-4" /> 一键拨号
                  </button>
                  <button className="inline-flex items-center gap-2 px-6 py-3 rounded-lg border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 shadow-lg hover:shadow-xl transition-all duration-300">
                    <MapPin className="w-4 h-4" /> 查看地图
                  </button>
                </div>
              </div>
              <div className="space-y-4">
                <div className="border border-gray-200 rounded-xl p-4 bg-white">
                  <h4 className="font-semibold text-gray-900 mb-3">服务统计</h4>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">月服务人次</span>
                      <span className="font-semibold">2,456人</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">成功率</span>
                      <span className="font-semibold text-green-600">92%</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">平均等待时间</span>
                      <span className="font-semibold">15分钟</span>
                    </div>
                  </div>
                </div>
                <div className="border border-gray-200 rounded-xl p-4 bg-white">
                  <h4 className="font-semibold text-gray-900 mb-3">办事指南</h4>
                  <div className="space-y-2">
                    <button className="flex items-center justify-between w-full p-2 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors text-left">
                      <span className="text-sm font-medium">就业登记</span>
                      <ArrowRight className="w-4 h-4 text-gray-400" />
                    </button>
                    <button className="flex items-center justify-between w-full p-2 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors text-left">
                      <span className="text-sm font-medium">职业介绍</span>
                      <ArrowRight className="w-4 h-4 text-gray-400" />
                    </button>
                    <button className="flex items-center justify-between w-full p-2 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors text-left">
                      <span className="text-sm font-medium">技能培训</span>
                      <ArrowRight className="w-4 h-4 text-gray-400" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-6 p-6 rounded-xl bg-gradient-to-r from-slate-50 to-gray-50 border border-slate-200 text-slate-800 text-sm leading-relaxed">
              <div className="font-bold mb-2 flex items-center gap-2">
                <Lightbulb className="w-4 h-4" />
                设计说明
              </div>
              <ul className="list-disc pl-5 space-y-1">
                <li><strong>机构展示：</strong>按城市/服务类型筛选；展示营业时间与办事指南链接。</li>
                <li><strong>便捷联系：</strong>集成导航与一键拨号，便于群众快速联系。</li>
                <li><strong>服务管理：</strong>新增服务统计、办事指南、等待时间等功能。</li>
                <li><strong>用户体验：</strong>提供详细的机构信息、服务类型、联系方式等，便于用户快速找到所需服务。</li>
                <li><strong>信息完整：</strong>包含地址、电话、营业时间、服务类型等完整信息，确保用户能够顺利获得服务。</li>
              </ul>
            </div>
          </ScreenCard>
        </section>
        {/* 实施路径（时间轴） */}
        <section className="py-12">
          <div className="relative overflow-hidden rounded-2xl border border-gray-200 bg-white p-8 shadow-[0_8px_32px_rgba(0,0,0,0.08)]">
            <div className="absolute -inset-24 opacity-10 bg-[radial-gradient(24rem_12rem_at_15%_20%,rgba(59,130,246,0.6),transparent),radial-gradient(24rem_12rem_at_85%_70%,rgba(168,85,247,0.6),transparent)]" />
            <div className="relative">
              <div className="flex items-center gap-2 text-primary-700 text-sm font-medium mb-2">
                <CheckCircle2 className="w-4 h-4" />
                <span>实施路径</span>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">一个月内交付的快速实施路径</h2>
              <p className="text-gray-600 mb-6">我们采用敏捷开发方法，将项目分为四个阶段，确保在一个月内完成从需求分析到最终交付的全流程，为客户提供高质量、可交付的解决方案。</p>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <div className="relative rounded-2xl border border-gray-200 bg-white p-6 hover:shadow-lg transition-all duration-300">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary-500 to-primary-600 text-white border border-primary-200 flex items-center justify-center font-bold text-sm shadow-lg">
                      1
                    </div>
                    <div className="font-bold text-gray-900">需求确认</div>
                  </div>
                  <div className="text-sm text-gray-600 leading-relaxed">快速对齐甲方KPI及评审要点，梳理数据来源与核验规则。深入了解客户需求，制定详细的项目计划和实施方案。</div>
                  <div className="mt-3 flex items-center gap-1 text-xs text-gray-500">
                    <Clock className="w-3 h-3" />
                    <span>3-5天</span>
                  </div>
                </div>
                <div className="relative rounded-2xl border border-gray-200 bg-white p-6 hover:shadow-lg transition-all duration-300">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary-500 to-primary-600 text-white border border-primary-200 flex items-center justify-center font-bold text-sm shadow-lg">
                      2
                    </div>
                    <div className="font-bold text-gray-900">设计开发</div>
                  </div>
                  <div className="text-sm text-gray-600 leading-relaxed">导入品牌规范，输出高保真稿与交互原型，快速确认。采用现代化设计语言，确保视觉效果的专业性和用户体验的流畅性。</div>
                  <div className="mt-3 flex items-center gap-1 text-xs text-gray-500">
                    <Clock className="w-3 h-3" />
                    <span>15-18天</span>
                  </div>
                </div>
                <div className="relative rounded-2xl border border-gray-200 bg-white p-6 hover:shadow-lg transition-all duration-300">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary-500 to-primary-600 text-white border border-primary-200 flex items-center justify-center font-bold text-sm shadow-lg">
                      3
                    </div>
                    <div className="font-bold text-gray-900">数据集成</div>
                  </div>
                  <div className="text-sm text-gray-600 leading-relaxed">建立字段映射与清洗规则，配置导入/校验/回退机制。确保数据的准确性和系统的稳定性，为后续运营提供可靠保障。</div>
                  <div className="mt-3 flex items-center gap-1 text-xs text-gray-500">
                    <Clock className="w-3 h-3" />
                    <span>5-7天</span>
                  </div>
                </div>
                <div className="relative rounded-2xl border border-gray-200 bg-white p-6 hover:shadow-lg transition-all duration-300">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary-500 to-primary-600 text-white border border-primary-200 flex items-center justify-center font-bold text-sm shadow-lg">
                      4
                    </div>
                    <div className="font-bold text-gray-900">测试交付</div>
                  </div>
                  <div className="text-sm text-gray-600 leading-relaxed">联调验收，提供培训与运维手册。确保系统稳定运行，为客户提供完善的技术支持和培训服务。</div>
                  <div className="mt-3 flex items-center gap-1 text-xs text-gray-500">
                    <Clock className="w-3 h-3" />
                    <span>3-5天</span>
                  </div>
                </div>
              </div>
              <div className="mt-8 p-6 rounded-xl bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-100">
                <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                  <Info className="w-4 h-4" />
                  快速交付保障
                </h3>
                <div className="text-sm text-gray-700 leading-relaxed space-y-2">
                  <p><strong>敏捷开发：</strong>采用敏捷开发方法，每周进行项目评审和客户沟通，及时调整项目方向，确保项目进度和质量。</p>
                  <p><strong>技术保障：</strong>使用成熟的技术栈和开发框架，确保系统的稳定性和可扩展性。提供完善的技术文档和运维支持。</p>
                  <p><strong>质量保证：</strong>建立完善的质量管理体系，包括代码审查、测试验证、用户验收等环节，确保交付质量。</p>
                  <p><strong>服务支持：</strong>提供7×24小时技术支持服务，确保系统稳定运行。定期进行系统维护和功能更新。</p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
